import { useEffect, useState } from 'react';
import { avatarSource } from '../lib/avatar-source.ts';
import { cx } from './ui.tsx';

/**
 * A person, shown as their picture or their initials.
 *
 * Initials are the default rather than a generic silhouette: in a system where
 * the same six people appear on every audit row, a column of identical grey
 * heads tells you nothing, while "AQ" and "RK" are legible at 24 pixels.
 *
 * The colour is derived from the name, so the same person is always the same
 * colour and it needs nothing stored. Hues are spaced far enough apart to stay
 * distinguishable and kept dark enough for white text to pass contrast.
 *
 * The picture is fetched rather than pointed at with a src, because the API
 * needs an Authorization header an <img> tag cannot send. See avatarSource.
 */

const SIZES = {
  sm: { box: 'h-7 w-7', text: 'text-[11px]' },
  md: { box: 'h-9 w-9', text: 'text-[13px]' },
  lg: { box: 'h-12 w-12', text: 'text-[17px]' },
  xl: { box: 'h-24 w-24', text: 'text-[34px]' },
} as const;

export function Avatar({
  name,
  userId,
  hasAvatar = false,
  /** Changes when the picture does, so the browser refetches it. */
  version,
  size = 'md',
  className,
}: {
  name: string;
  userId?: string | undefined;
  hasAvatar?: boolean;
  version?: string | undefined;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  // Null means "no picture, show initials" — either because there is none
  // or because it could not be loaded. Recomputed whenever the person or the
  // version changes, so a newly uploaded picture appears without a reload.
  // The previous version of this held a sticky `failed` flag that never
  // reset, which meant one failure hid the picture until the component
  // happened to unmount.
  const [source, setSource] = useState<string | null>(null);
  const dimensions = SIZES[size];

  useEffect(() => {
    if (!hasAvatar || !userId) {
      setSource(null);
      return;
    }

    let cancelled = false;
    void avatarSource(userId, version).then((url) => {
      if (!cancelled) setSource(url);
    });

    return () => {
      cancelled = true;
    };
  }, [hasAvatar, userId, version]);

  const showImage = Boolean(source);

  return (
    <span
      className={cx(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full',
        dimensions.box,
        className,
      )}
      style={showImage ? undefined : { backgroundColor: colourFor(name) }}
      title={name}
    >
      {source ? (
        <img src={source} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className={cx('font-semibold leading-none text-white', dimensions.text)}>
          {initialsOf(name)}
        </span>
      )}
    </span>
  );
}

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0] as string;
  if (words.length === 1) return first.slice(0, 2).toUpperCase();
  const last = words.at(-1) as string;
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
}

function colourFor(name: string): string {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) | 0;
  }
  // 137.5° apart — the golden angle, which spreads successive hues about as
  // far as they can be spread.
  const hue = Math.abs(hash * 137.5) % 360;
  return `hsl(${hue.toFixed(0)} 42% 38%)`;
}
